(() => {
  const ROOT_SELECTOR = "[data-universal-product-option-selector]";

  class UniversalProductOptionSelector {
    constructor(container) {
      this.container = container;
      this.config = this.#readConfig();
      this.instanceId = container.dataset.instanceId || Math.random().toString(36).slice(2);
      this.productFormWrapper = container.closest(".product-form");
      this.form =
        document.getElementById(this.config?.productFormId || "") ||
        container.closest("form") ||
        this.productFormWrapper?.querySelector('form[is="product-form"]') ||
        this.productFormWrapper?.querySelector('form[action*="/cart/add"]');
      this.variantInput = this.form?.querySelector('input[name="id"]') || null;
      this.summary = container.querySelector("[data-universal-option-summary]");
      this.variants = Array.isArray(this.config?.variants) ? this.config.variants : [];
      this.optionNames = Array.isArray(this.config?.optionNames) ? this.config.optionNames : [];
      this.separator = String(this.config?.composedSeparator || "+").trim() || "+";
      this.steps = [];
      this.state = {};
      this.fixedOptionValues = {};

      if (!this.config || !this.variants.length || !this.form || !this.variantInput) {
        this.#fail(this.config?.messages?.configuration || "Selector configuration is incomplete.");
        return;
      }

      this.steps = this.#prepareSteps(this.config.steps || []);
      if (!this.steps.length || !this.#validateMappings()) return;

      const initialVariant = this.#getInitialVariant();
      if (!initialVariant) {
        this.#fail(this.config?.messages?.unavailable || "Unavailable");
        return;
      }

      const mappedOptionIndices = new Set(this.steps.map((step) => step.optionIndex));
      this.optionNames.forEach((_name, index) => {
        if (!mappedOptionIndices.has(index)) {
          this.fixedOptionValues[index] = String(initialVariant.options?.[index] || "");
        }
      });

      this.state = this.#stateFromVariant(initialVariant);
      this.#attachEvents();
      this.#render();
    }

    #readConfig() {
      const element = this.container.querySelector("[data-universal-option-config]");
      if (!element) return null;

      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.error("Universal option selector: invalid JSON configuration", error);
        return null;
      }
    }

    #prepareSteps(rawSteps) {
      return rawSteps
        .filter((step) => step && step.enabled !== false)
        .map((step, index) => {
          const configuredName = String(step.optionName || "").trim();
          const fallbackOptionIndex = Math.max(Number(step.optionPosition || index + 1) - 1, 0);
          const optionName = configuredName || this.optionNames[fallbackOptionIndex] || "";
          const optionIndex = this.optionNames.indexOf(optionName);

          return {
            key: String(step.key || `step-${index + 1}`),
            position: index,
            optionName,
            optionIndex,
            mappingType: step.mappingType === "composed" ? "composed" : "direct",
            componentIndex: null,
            layout: step.layout === "compact" ? "compact" : "visual",
            overrides: this.#parseOverrides(step.valueOverrides),
            values: [],
          };
        });
    }

    #validateMappings() {
      const stepsByOption = new Map();

      for (const step of this.steps) {
        if (step.optionIndex < 0) {
          this.#fail(`${this.config?.messages?.unknownOption || "Unknown Shopify option"}: ${step.optionName}`);
          return false;
        }
        if (!stepsByOption.has(step.optionIndex)) stepsByOption.set(step.optionIndex, []);
        stepsByOption.get(step.optionIndex).push(step);
      }

      for (const [optionIndex, optionSteps] of stepsByOption.entries()) {
        const directSteps = optionSteps.filter((step) => step.mappingType === "direct");
        const composedSteps = optionSteps.filter((step) => step.mappingType === "composed");

        if (directSteps.length > 1 || (directSteps.length && composedSteps.length)) {
          this.#fail(this.config?.messages?.configuration || "Selector configuration is incomplete.");
          return false;
        }

        if (composedSteps.length) {
          if (composedSteps.length < 2) {
            this.#fail(this.config?.messages?.configuration || "Selector configuration is incomplete.");
            return false;
          }

          composedSteps.forEach((step, index) => {
            step.componentIndex = index;
          });

          const invalidVariant = this.variants.some((variant) => {
            const parts = this.#splitComposedValue(variant.options?.[optionIndex]);
            return parts.length !== composedSteps.length;
          });

          if (invalidVariant) {
            this.#fail(this.config?.messages?.configuration || "Selector configuration is incomplete.");
            return false;
          }
        }
      }

      this.steps.forEach((step) => {
        const values = [];
        this.variants.forEach((variant) => {
          const value = this.#stepValueFromVariant(step, variant);
          if (value && !values.includes(value)) values.push(value);
        });
        step.values = values;
      });

      if (this.steps.some((step) => !step.values.length)) {
        this.#fail(this.config?.messages?.configuration || "Selector configuration is incomplete.");
        return false;
      }

      return true;
    }

    #parseOverrides(rawValue) {
      const overrides = {};

      String(rawValue || "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .forEach((line) => {
          const [
            value = "",
            title = "",
            subtitle = "",
            description = "",
            image = "",
            recommendations = "",
            badge = "",
            secondaryImage = "",
            secondaryTitle = "",
            secondaryRecommendations = "",
          ] = line
            .split("|")
            .map((part) => part.trim());
          if (!value) return;
          const metadata = {
            title,
            subtitle,
            description,
            image,
            recommendations,
            badge,
            secondaryImage,
            secondaryTitle,
            secondaryRecommendations,
          };
          overrides[value] = metadata;

          // ComfortPro was renamed to ComfortSport — keep both keys working.
          const aliases = this.#valueAliases(value);
          aliases.forEach((alias) => {
            if (overrides[alias]) return;
            const aliasedMetadata = { ...metadata };
            if (aliasedMetadata.title === value) {
              aliasedMetadata.title = alias;
            }
            overrides[alias] = aliasedMetadata;
          });
        });

      return overrides;
    }

    #valueAliases(value) {
      const aliases = [];
      const replacements = [
        ["ComfortPro", "ComfortSport"],
        ["ComfortSport", "ComfortPro"],
      ];

      replacements.forEach(([from, to]) => {
        if (value.includes(from)) {
          aliases.push(value.split(from).join(to));
        }
      });

      return aliases.filter((alias) => alias && alias !== value);
    }

    #getInitialVariant() {
      const requestedId = new URLSearchParams(window.location.search).get("variant");
      const candidateIds = [requestedId, this.variantInput?.value, this.config?.currentVariantId]
        .filter(Boolean)
        .map(String);

      for (const id of candidateIds) {
        const variant = this.variants.find((candidate) => String(candidate.id) === id);
        if (variant) return variant;
      }

      return this.variants.find((variant) => variant.available) || this.variants[0] || null;
    }

    #stateFromVariant(variant) {
      const state = {};
      this.steps.forEach((step) => {
        state[step.key] = this.#stepValueFromVariant(step, variant);
      });
      return state;
    }

    #stepValueFromVariant(step, variant) {
      const nativeValue = String(variant?.options?.[step.optionIndex] || "").trim();
      if (step.mappingType === "direct") return nativeValue;
      return this.#splitComposedValue(nativeValue)[step.componentIndex] || "";
    }

    #splitComposedValue(value) {
      return String(value || "")
        .split(this.separator)
        .map((part) => part.trim());
    }

    #attachEvents() {
      this.container.addEventListener("change", (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.matches("[data-universal-step-input]")) return;

        const stepIndex = Number(input.dataset.stepIndex);
        const step = this.steps[stepIndex];
        if (!step) return;

        this.state[step.key] = input.value;
        const matchingVariant = this.#findVariantThroughStep(stepIndex, true) || this.#findVariantThroughStep(stepIndex, false);
        if (matchingVariant) this.state = this.#stateFromVariant(matchingVariant);
        this.#render();
      });
    }

    #render() {
      this.steps.forEach((step, stepIndex) => {
        const target = this.container.querySelector(`[data-universal-step-options="${step.key}"]`);
        if (!target) return;

        target.dataset.layout = step.layout;
        target.style.setProperty("--universal-option-columns", String(Math.min(Math.max(step.values.length, 1), 4)));
        target.innerHTML = step.values.map((value) => this.#buildOptionMarkup(step, stepIndex, value)).join("");
      });

      this.#updateVariantState();
    }

    #buildOptionMarkup(step, stepIndex, value) {
      const id = `universal-option-${this.#slug(this.instanceId)}-${this.#slug(step.key)}-${this.#slug(value)}`;
      const checked = this.state[step.key] === value;
      const available = this.#isValueAvailable(stepIndex, value);
      const metadata = this.#resolveOptionMetadata(step, stepIndex, value);
      const title = metadata.title || value;

      if (step.layout === "compact") {
        return `
          <div class="universal-option-selector__option">
            <input class="universal-option-selector__radio" type="radio" id="${id}" name="universal-option-${this.#escape(this.instanceId)}-${this.#escape(step.key)}" value="${this.#escape(value)}" data-universal-step-input data-step-index="${stepIndex}" ${checked ? "checked" : ""} ${available || checked ? "" : "disabled"}>
            <label class="universal-option-selector__card universal-option-selector__card--compact" for="${id}">
              <span class="universal-option-selector__dot" aria-hidden="true"></span>
              <span>${this.#escape(title)}</span>
            </label>
          </div>`;
      }

      const image = this.#resolveCardImage(stepIndex, value, step.overrides[value] || {});
      const imageMarkup = image
        ? `<img src="${this.#escape(this.#normalizeImageUrl(image))}" alt="${this.#escape(title)}" width="240" height="180" loading="lazy">`
        : "";
      const subtitleMarkup = metadata.subtitle
        ? `<div class="universal-option-selector__subtitle">${this.#escape(metadata.subtitle)}</div>`
        : "";
      const descriptionMarkup = metadata.description
        ? `<div class="universal-option-selector__description">${this.#escape(metadata.description)}</div>`
        : "";
      const recommendationMarkup = this.#buildRecommendationMarkup(metadata.recommendations);
      const badgeMarkup = metadata.badge
        ? `<span class="universal-option-selector__badge">${this.#escape(metadata.badge)}</span>`
        : "";
      const cardKind = metadata.recommendations ? "recommendation" : "model";

      return `
        <div class="universal-option-selector__option">
          <input class="universal-option-selector__radio" type="radio" id="${id}" name="universal-option-${this.#escape(this.instanceId)}-${this.#escape(step.key)}" value="${this.#escape(value)}" data-universal-step-input data-step-index="${stepIndex}" ${checked ? "checked" : ""} ${available || checked ? "" : "disabled"}>
          <label class="universal-option-selector__card universal-option-selector__card--visual universal-option-selector__card--${cardKind}" for="${id}">
            ${badgeMarkup}
            <div class="universal-option-selector__image">
              <span class="universal-option-selector__check" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path></svg></span>
              ${imageMarkup}
            </div>
            <div class="universal-option-selector__info">
              <div class="universal-option-selector__name">${this.#escape(title)}</div>
              ${subtitleMarkup}
              ${descriptionMarkup}
              ${recommendationMarkup}
            </div>
          </label>
        </div>`;
    }

    #resolveOptionMetadata(step, stepIndex, value) {
      const metadata = { ...(step.overrides[value] || {}) };
      const useSecondary = this.#shouldUseSecondaryOverrideImage(stepIndex);

      if (useSecondary) {
        if (metadata.secondaryTitle) metadata.title = metadata.secondaryTitle;
        if (metadata.secondaryRecommendations) {
          metadata.recommendations = metadata.secondaryRecommendations;
        }
      }

      return metadata;
    }

    #buildRecommendationMarkup(recommendations) {
      const bullets = String(recommendations || "")
        .split(",")
        .map((bullet) => bullet.trim())
        .filter(Boolean)
        .map((bullet) => `<li>&bull; ${this.#escape(bullet)}</li>`)
        .join("");

      if (!bullets) return "";

      const label = String(this.config?.recommendationLabel || "Empfohlen für:").trim();
      return `
        <div class="universal-option-selector__recommendation">
          ${label ? `<span class="universal-option-selector__recommendation-label">${this.#escape(label)}</span>` : ""}
          <ul class="universal-option-selector__recommendation-list">${bullets}</ul>
        </div>`;
    }

    #isValueAvailable(stepIndex, value) {
      const step = this.steps[stepIndex];
      const candidateState = { ...this.state, [step.key]: value };
      return this.variants.some(
        (variant) => variant.available && this.#variantMatches(variant, candidateState, stepIndex),
      );
    }

    #findVariantThroughStep(stepIndex, availableOnly) {
      return this.variants.find(
        (variant) => (!availableOnly || variant.available) && this.#variantMatches(variant, this.state, stepIndex),
      ) || null;
    }

    #findExactVariant() {
      return this.variants.find((variant) => this.#variantMatches(variant, this.state, this.steps.length - 1)) || null;
    }

    #variantMatches(variant, state, throughStepIndex) {
      for (const [optionIndex, value] of Object.entries(this.fixedOptionValues)) {
        if (String(variant.options?.[Number(optionIndex)] || "") !== value) return false;
      }

      for (let index = 0; index <= throughStepIndex; index += 1) {
        const step = this.steps[index];
        if (!step) continue;
        const selectedValue = state[step.key];
        if (selectedValue && this.#stepValueFromVariant(step, variant) !== selectedValue) return false;
      }

      return true;
    }

    #resolveCardImage(stepIndex, value, metadata) {
      const primaryImage = String(metadata?.image || "").trim();
      const secondaryImage = String(metadata?.secondaryImage || "").trim();

      if (secondaryImage && this.#shouldUseSecondaryOverrideImage(stepIndex)) {
        return secondaryImage;
      }

      if (primaryImage) return primaryImage;
      return this.#imageForValue(stepIndex, value);
    }

    #shouldUseSecondaryOverrideImage(stepIndex) {
      if (stepIndex <= 0) return false;

      const modelStep = this.steps[0];
      if (!modelStep) return false;

      const selectedModel = this.state[modelStep.key];
      if (!selectedModel) return false;

      // Prefer the first value listed in Step 1 overrides as the primary model.
      const overrideKeys = Object.keys(modelStep.overrides || {});
      const primaryModel = overrideKeys[0] || modelStep.values?.[0];
      if (!primaryModel) return false;

      if (selectedModel === primaryModel) return false;

      const primaryAliases = this.#valueAliases(primaryModel);
      if (primaryAliases.includes(selectedModel)) return false;

      return true;
    }

    #imageForValue(stepIndex, value) {
      const step = this.steps[stepIndex];
      const candidateState = { ...this.state, [step.key]: value };
      const candidates = this.variants.filter((variant) => this.#variantMatches(variant, candidateState, stepIndex));
      const variant = candidates.find((candidate) => candidate.available && this.#variantImage(candidate)) ||
        candidates.find((candidate) => this.#variantImage(candidate));
      return this.#variantImage(variant);
    }

    #variantImage(variant) {
      const image =
        variant?.featured_media?.preview_image?.src ||
        variant?.featured_media?.src ||
        variant?.featured_image?.src ||
        variant?.image?.src ||
        variant?.image ||
        "";
      return typeof image === "string" ? image : "";
    }

    #normalizeImageUrl(url) {
      return String(url || "").startsWith("//") ? `https:${url}` : String(url || "");
    }

    #updateVariantState() {
      const variant = this.#findExactVariant();

      if (variant) {
        this.variantInput.disabled = false;
        this.variantInput.value = variant.id;
      } else {
        this.variantInput.disabled = true;
      }

      this.#updateSummary(variant);
      this.#updateUrl(variant);
      this.#dispatchVariantChange(variant);
      this.#showVariantMedia(variant);
    }

    #updateSummary(variant) {
      if (!this.summary) return;

      if (!variant) {
        this.summary.textContent = this.config?.messages?.unavailable || "Unavailable";
        this.summary.setAttribute("aria-invalid", "true");
        return;
      }

      const values = this.steps.map((step) => this.state[step.key]).filter(Boolean).join(" · ");
      const label = String(this.config?.summaryLabel || "").trim();
      this.summary.textContent = label ? `${label} ${values}` : values;
      this.summary.removeAttribute("aria-invalid");
    }

    #updateUrl(variant) {
      if (!variant?.id || !window.history?.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({ path: url.toString() }, "", url.toString());
    }

    #dispatchVariantChange(variant) {
      if (!this.form) return;

      let eventVariant = variant;
      if (variant && !variant.featured_media && (variant.featured_image || variant.image)) {
        eventVariant = { ...variant, featured_media: variant.featured_image || variant.image };
      }

      this.form.dispatchEvent(new CustomEvent("variant:changed", {
        bubbles: true,
        detail: { variant: eventVariant },
      }));
    }

    #showVariantMedia(variant) {
      const mediaId = variant?.featured_media?.id || variant?.image?.id || variant?.featured_image?.id;
      if (!mediaId) return;

      const section = this.container.closest(".shopify-section");
      const slider = section?.querySelector('[id*="-media-list"][data-transition-effect]') ||
        document.querySelector('[id*="-media-list"][data-transition-effect]');
      if (!slider?.swiper) return;
      const target = slider.querySelector(`.swiper-slide[data-media-id="${mediaId}"]:not(.is-filtered)`);
      if (!target) return;

      const visibleSlides = Array.from(slider.querySelectorAll(".swiper-slide:not(.is-filtered)"));
      const index = visibleSlides.indexOf(target);
      if (index >= 0) slider.swiper.slideTo(index);
    }

    #fail(message) {
      console.error("Universal option selector:", message);
      this.container.classList.add("universal-option-selector--invalid");
      if (this.summary) {
        this.summary.textContent = message;
        this.summary.setAttribute("aria-invalid", "true");
      }
    }

    #slug(value) {
      return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    }

    #escape(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }
  }

  const initUniversalProductOptionSelectors = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((container) => {
      if (container.dataset.universalOptionSelectorInitialized === "true") return;
      container.dataset.universalOptionSelectorInitialized = "true";
      new UniversalProductOptionSelector(container);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initUniversalProductOptionSelectors());
  } else {
    initUniversalProductOptionSelectors();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initUniversalProductOptionSelectors(event.target);
  });
})();
